"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, SlidersHorizontal as Sliders, Sun, Thermometer, Zap, CircleCheck as CheckCircle2, MessageCircle, Smartphone, ShieldCheck, TrendingDown, Layers, Play } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useCookieConsent } from "@/hooks/useCookieConsent";
type Language = 'es' | 'en' | 'eu' | 'fr';
const LANGUAGE_STORAGE_KEY = 'wvc_language';
const LANGUAGE_EVENT_NAME = 'wvc-language-change';
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    try {
      if (typeof window !== "undefined") {
        const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
        if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
          return stored;
        }
      }
    } catch {
      // fallback
    }
    return "es";
  });
  useEffect(() => {
    const handleLanguageChange = (event: Event) => {
      try {
        const customEvent = event as CustomEvent<Language>;
        if (customEvent.detail && ["es", "en", "eu", "fr"].includes(customEvent.detail)) {
          setLanguageState(customEvent.detail);
        } else {
          const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
          if (stored === "es" || stored === "en" || stored === "eu" || stored === "fr") {
            setLanguageState(stored);
          }
        }
      } catch {
        // ignore
      }
    };
    window.addEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
    window.addEventListener("storage", handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, []);
  const setLanguage = (newLang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: newLang
      }));
    } catch {
      // ignore
    }
    setLanguageState(newLang);
  };
  return [language, setLanguage];
}
const SCENE_CONFIG = [{
  id: "relax",
  lux: "280 lm",
  temp: "21,5 °C",
  power: "0,4 kWh",
  accentGradient: "from-amber-500/20 via-emerald-500/10 to-transparent"
}, {
  id: "cine",
  lux: "40 lm",
  temp: "21,0 °C",
  power: "0,6 kWh",
  accentGradient: "from-blue-600/20 via-emerald-500/10 to-transparent"
}, {
  id: "eficiencia",
  lux: "520 lm",
  temp: "22,0 °C",
  power: "0,1 kWh",
  accentGradient: "from-emerald-500/25 via-teal-500/10 to-transparent"
}, {
  id: "clima",
  lux: "350 lm",
  temp: "20,5 °C",
  power: "0,3 kWh",
  accentGradient: "from-cyan-500/20 via-emerald-500/10 to-transparent"
}];
const FEATURE_ICONS = [Layers, Smartphone, TrendingDown, ShieldCheck];
const translations = {
  es: {
    badge: "DESTACADO • Domótica Loxone & Smart Home",
    heading: "Domótica Loxone: tu casa, con inteligencia propia",
    intro: "Somos especialistas en automatización Loxone. Prueba el simulador de escenas y descubre cómo transformamos la atmósfera de tu hogar con un solo toque.",
    simTitle: "Simulador de Escenas Loxone",
    controlActive: "Control Activo",
    selectScene: "Selecciona una escena para probar:",
    telemetry: {
      lux: "LUZ",
      temp: "CLIMA",
      power: "CONSUMO"
    },
    scenes: {
      relax: {
        label: "Modo Relax",
        description: "Luz cálida tenue, persianas al 60% y música ambiental. El hogar respira contigo."
      },
      cine: {
        label: "Modo Cine",
        description: "Iluminación indirecta atenuada, persianas cerradas al 100% y acústica optimizada para proyectores."
      },
      eficiencia: {
        label: "Eficiencia",
        description: "Aprovechamiento máximo de radiación solar natural, desconexión de cargas pasivas y climatización eco."
      },
      clima: {
        label: "Clima Inteligente",
        description: "Gestión térmica zonificada en tiempo real basada en ocupación y previsión meteorológica de Bilbao."
      }
    },
    videoHeading: "Descubre Loxone en acción",
    videoConsentNotice: "Al reproducir este vídeo se carga contenido de YouTube (Google LLC / Google Ireland), que puede instalar cookies y tratar su dirección IP conforme a su política de privacidad. No se carga ningún dato previamente.",
    videoLoadOnce: "Cargar solo este vídeo",
    videoConfigureCookies: "Configurar cookies",
    videoCookiePolicyLink: "Política de Cookies",
    servicesHeading: "Servicios eléctricos de precisión",
    servicesDesc: "De una pequeña reparación a la reforma eléctrica completa de tu negocio. Un solo equipo, una sola llamada.",
    features: ["Tu hogar piensa por ti: persianas, clima, música y seguridad en un solo ecosistema", "Control total desde pulsadores Touch Pure o desde tu móvil, estés donde estés", "Optimización energética real: reduce hasta un 40% tu factura eléctrica", "Instalación certificada y puesta en marcha llave en mano por nuestro equipo"],
    whatsappBadge: "Asesoramiento Directo WhatsApp",
    whatsappParagraph: "¿Quieres transformar tu hogar en Bilbao, sus alrededores y en todo el País Vasco con la garantía oficial Loxone Partner?",
    whatsappButton: "Quiero domotizar mi casa",
    controlFooter: "Todo lo eléctrico, bajo control"
  },
  en: {
    badge: "FEATURED • Loxone Home Automation & Smart Home",
    heading: "Loxone Home Automation: your home, with its own intelligence",
    intro: "We are Loxone automation specialists. Try the scene simulator and discover how we transform your home's atmosphere with a single touch.",
    simTitle: "Loxone Scene Simulator",
    controlActive: "Active Control",
    selectScene: "Select a scene to test:",
    telemetry: {
      lux: "LIGHT",
      temp: "CLIMATE",
      power: "POWER"
    },
    scenes: {
      relax: {
        label: "Relax Mode",
        description: "Soft warm lighting, blinds at 60%, and ambient music. Your home breathes with you."
      },
      cine: {
        label: "Cinema Mode",
        description: "Dimmed indirect lighting, blinds 100% closed, and acoustics optimized for projectors."
      },
      eficiencia: {
        label: "Efficiency",
        description: "Maximum use of natural solar radiation, passive load shedding, and eco-friendly climate control."
      },
      clima: {
        label: "Smart Climate",
        description: "Real-time zoned thermal management based on occupancy and Bilbao weather forecasts."
      }
    },
    videoHeading: "Discover Loxone in action",
    videoConsentNotice: "Playing this video loads content from YouTube (Google LLC / Google Ireland), which may set cookies and process your IP address under its privacy policy. No data is loaded beforehand.",
    videoLoadOnce: "Load this video only once",
    videoConfigureCookies: "Cookie settings",
    videoCookiePolicyLink: "Cookie Policy",
    servicesHeading: "Precision electrical services",
    servicesDesc: "From a minor repair to the complete electrical renovation of your business. One team, one call.",
    features: ["Your home thinks for you: blinds, climate, music, and security in a single ecosystem", "Complete control from Touch Pure switches or your mobile phone, wherever you are", "Real energy optimization: reduce your electricity bill by up to 40%", "Certified installation and turnkey commissioning by our team"],
    whatsappBadge: "Direct WhatsApp Advice",
    whatsappParagraph: "Ready to upgrade your home in Bilbao, surrounding areas, and throughout the Basque Country with the official Loxone Partner guarantee?",
    whatsappButton: "I want to automate my home",
    controlFooter: "Everything electrical, under control"
  },
  eu: {
    badge: "NABARMENDUA • Loxone Domotika eta Smart Home",
    heading: "Loxone Domotika: zure etxea, berezko adimenarekin",
    intro: "Loxone automatizazioan espezialistak gara. Probatu eszena-simulagailua eta ezagutu nola eraldatzen dugun zure etxeko giroa ukitu bakarrarekin.",
    simTitle: "Loxone Eszena Simulagailua",
    controlActive: "Kontrol Aktiboa",
    selectScene: "Hautatu eszena bat probatzeko:",
    telemetry: {
      lux: "ARGIA",
      temp: "KLIMA",
      power: "KONTSUMOA"
    },
    scenes: {
      relax: {
        label: "Erlaxazio Modua",
        description: "Argi epel leuna, pertsianak %60an eta giro-musika. Etxeak zurekin batera arnasten du."
      },
      cine: {
        label: "Zinema Modua",
        description: "Zeharkako argiztapen leundua, pertsianak %100 itxita eta proiektagailuentzako akustika optimizatua."
      },
      eficiencia: {
        label: "Eraginkortasuna",
        description: "Eguzki-erradiazio naturalaren etekin handiena, karga pasiboen deskonexioa eta eko-klimatizazioa."
      },
      clima: {
        label: "Klima Adimenduna",
        description: "Eremukako kudeaketa termikoa denbora errealean, okupazioan eta Bilboko eguraldi-aurreikuspenean oinarrituta."
      }
    },
    videoHeading: "Ezagutu Loxone martxan",
    videoConsentNotice: "Bideo hau erreproduzitzean YouTube-ko (Google LLC / Google Ireland) edukia kargatzen da, cookie-ak ezar ditzakeena eta zure IP helbidea bere pribatutasun-politikaren arabera tratatu dezakeena. Ez da aldez aurretik daturik kargatzen.",
    videoLoadOnce: "Bideo hau bakarrik kargatu",
    videoConfigureCookies: "Cookie-ak konfiguratu",
    videoCookiePolicyLink: "Cookie Politika",
    servicesHeading: "Zehaztasunezko zerbitzu elektrikoak",
    servicesDesc: "Konponketa txiki batetik zure negozioaren erreforma elektriko oso batera. Talde bakarra, dei bakarra.",
    features: ["Zure etxeak zure ordez pentsatzen du: pertsianak, klima, musika eta segurtasuna ekosistema bakarrean", "Kontrol osoa Touch Pure pultsadoreetatik edo mugikorretik, zauden lekuan zaudela", "Benetako optimizazio energetikoa: murriztu zure faktura elektrikoa %40raino", "Instalazio ziurtatua eta giltza eskuan abian jartzea gure taldearen eskutik"],
    whatsappBadge: "Aholkularitza Zuzena WhatsApp Bidez",
    whatsappParagraph: "Zure etxea Bilbon, inguruetan eta Euskal Herri osoan eraldatu nahi duzu Loxone Partner berme ofizialarekin?",
    whatsappButton: "Nire etxea domotizatu nahi dut",
    controlFooter: "Guzti elektrikoa, kontrolpean"
  },
  fr: {
    badge: "EN VEDETTE • Domotique Loxone & Smart Home",
    heading: "Domotique Loxone : votre maison, avec sa propre intelligence",
    intro: "Nous sommes spécialistes de l'automatisation Loxone. Essayez le simulateur de scènes et découvrez comment nous transformons l'atmosphère de votre maison d'un simple geste.",
    simTitle: "Simulateur de Scènes Loxone",
    controlActive: "Contrôle Actif",
    selectScene: "Sélectionnez une scène à tester :",
    telemetry: {
      lux: "LUMIÈRE",
      temp: "CLIMAT",
      power: "CONSOMMATION"
    },
    scenes: {
      relax: {
        label: "Mode Relax",
        description: "Lumière douce et chaude, stores à 60 % et musique d'ambiance. La maison respire avec vous."
      },
      cine: {
        label: "Mode Cinéma",
        description: "Éclairage indirect tamisé, stores fermés à 100 % et acoustique optimisée pour projecteurs."
      },
      eficiencia: {
        label: "Efficacité",
        description: "Exploitation maximale du rayonnement solaire naturel, déconnexion des charges passives et climatisation éco."
      },
      clima: {
        label: "Climat Intelligent",
        description: "Gestion thermique zonée en temps réel basée sur l'occupation et les prévisions météo de Bilbao."
      }
    },
    videoHeading: "Découvrez Loxone en action",
    videoConsentNotice: "La lecture de cette vidéo charge du contenu depuis YouTube (Google LLC / Google Ireland), qui peut déposer des cookies et traiter votre adresse IP selon sa politique de confidentialité. Aucune donnée n'est chargée au préalable.",
    videoLoadOnce: "Charger uniquement cette vidéo",
    videoConfigureCookies: "Configurer les cookies",
    videoCookiePolicyLink: "Politique de cookies",
    servicesHeading: "Services électriques de précision",
    servicesDesc: "D'une petite réparation à la rénovation électrique complète de votre entreprise. Une seule équipe, un seul appel.",
    features: ["Votre maison pense pour vous : stores, climatisation, musique et sécurité dans un seul écosystème", "Contrôle total depuis les interrupteurs Touch Pure ou votre mobile, où que vous soyez", "Optimisation énergétique réelle : réduisez jusqu'à 40 % votre facture d'électricité", "Installation certifiée et mise en service clé en main par notre équipe"],
    whatsappBadge: "Conseil Direct WhatsApp",
    whatsappParagraph: "Vous souhaitez transformer votre maison à Bilbao, ses environs et dans tout le Pays Basque avec la garantie officielle Loxone Partner ?",
    whatsappButton: "Je veux domotiser ma maison",
    controlFooter: "Tout l'électrique, sous contrôle"
  }
};
export default function Domotica() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const {
    thirdPartyAllowed,
    openPreferences
  } = useCookieConsent();
  const [activeModeId, setActiveModeId] = useState<string>("relax");
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [loadOnceConfirmed, setLoadOnceConfirmed] = useState<boolean>(false);
  const [showConsentPrompt, setShowConsentPrompt] = useState<boolean>(false);
  const canPlay = isPlaying && (thirdPartyAllowed || loadOnceConfirmed);
  const handlePlayClick = () => {
    if (thirdPartyAllowed || loadOnceConfirmed) {
      setIsPlaying(true);
    } else {
      setShowConsentPrompt(true);
    }
  };
  const handleLoadOnce = () => {
    setLoadOnceConfirmed(true);
    setIsPlaying(true);
    setShowConsentPrompt(false);
  };
  const sceneModes = SCENE_CONFIG.map(config => {
    const sceneTrans = t.scenes[config.id as keyof typeof t.scenes] || translations.es.scenes[config.id as keyof typeof translations.es.scenes];
    return {
      ...config,
      label: sceneTrans.label,
      description: sceneTrans.description
    };
  });
  const activeMode = sceneModes.find(m => m.id === activeModeId) || sceneModes[0];
  return <section id="domótica-loxone-tu-casa-con-inteligencia-propia" data-nav="dark" className="bg-background text-foreground relative overflow-hidden py-20 sm:py-28 lg:py-32">
      {/* Decorative Technical Grid Overlay */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,var(--border),transparent_50%),radial-gradient(circle_at_bottom_left,rgba(152,248,180,0.05),transparent_40%)] opacity-70" aria-hidden="true" />

      <div className="relative mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-8">
        
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1 text-xs font-mono font-semibold tracking-widest text-primary uppercase mb-4">
            <Sparkles className="size-3.5 text-primary" />
            <span>{t.badge}</span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground font-default mb-4">
            {t.heading}
          </h2>
          
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            {t.intro}
          </p>
        </div>

        {/* Content Layout - 12 Column Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Interactive Loxone Scene Simulator - 7 Cols */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-7 flex flex-col gap-6">
            <Card className="bg-card/90 border-border/60 shadow-2xl overflow-hidden backdrop-blur-sm">
              <CardHeader className="border-b border-border/40 pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <span className="size-3 rounded-full bg-primary animate-pulse" />
                    <CardTitle className="text-sm font-mono tracking-wider text-muted-foreground uppercase">
                      {t.simTitle}
                    </CardTitle>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground bg-secondary/80 px-2.5 py-1 rounded-md border border-white/5">
                    <Sliders className="size-3.5 text-primary" />
                    <span>{t.controlActive}</span>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                {/* Visual Preview Window */}
                <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden bg-slate-950">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-led.jpg" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-led.jpg-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-led.jpg-wvc-sizes" alt="Interior domotizado con Loxone" loading="lazy" className="size-full object-cover transition-opacity duration-700" />

                  {/* Gradient Scrim & Dynamic Lighting Mood Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                  <div className={`absolute inset-0 bg-gradient-to-br ${activeMode.accentGradient} transition-all duration-700`} />

                  {/* Active Scene Overlay Badge */}
                  <div className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-background/80 backdrop-blur-md border border-white/10 rounded-lg px-3 py-1.5 flex items-center gap-2">
                    <span className="size-2 rounded-full bg-primary" />
                    <span className="text-xs font-mono font-bold tracking-wide text-foreground">
                      {activeMode.label}
                    </span>
                  </div>

                  {/* Live Telemetry Bar Overlay */}
                  <div className="absolute bottom-4 left-4 right-4 sm:bottom-6 sm:left-6 sm:right-6 bg-card/90 backdrop-blur-md border border-border/80 rounded-xl p-3 sm:p-4 shadow-xl">
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 text-center divide-x divide-border/50">
                      
                      <div className="flex flex-col items-center justify-center px-1">
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-mono font-medium text-muted-foreground uppercase">
                          <Sun className="size-3 text-primary" /> {t.telemetry.lux}
                        </span>
                        <AnimatePresence mode="wait">
                          <motion.span key={activeMode.id + "-lux"} initial={{
                          opacity: 0,
                          y: 4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -4
                        }} transition={{
                          duration: 0.2
                        }} className="text-xs sm:text-sm md:text-base font-mono font-bold text-foreground mt-0.5">
                            {activeMode.lux}
                          </motion.span>
                        </AnimatePresence>
                      </div>

                      <div className="flex flex-col items-center justify-center px-1">
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-mono font-medium text-muted-foreground uppercase">
                          <Thermometer className="size-3 text-primary" /> {t.telemetry.temp}
                        </span>
                        <AnimatePresence mode="wait">
                          <motion.span key={activeMode.id + "-temp"} initial={{
                          opacity: 0,
                          y: 4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -4
                        }} transition={{
                          duration: 0.2
                        }} className="text-xs sm:text-sm md:text-base font-mono font-bold text-foreground mt-0.5">
                            {activeMode.temp}
                          </motion.span>
                        </AnimatePresence>
                      </div>

                      <div className="flex flex-col items-center justify-center px-1">
                        <span className="inline-flex items-center gap-1 text-[10px] sm:text-xs font-mono font-medium text-muted-foreground uppercase">
                          <Zap className="size-3 text-primary" /> {t.telemetry.power}
                        </span>
                        <AnimatePresence mode="wait">
                          <motion.span key={activeMode.id + "-power"} initial={{
                          opacity: 0,
                          y: 4
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} exit={{
                          opacity: 0,
                          y: -4
                        }} transition={{
                          duration: 0.2
                        }} className="text-xs sm:text-sm md:text-base font-mono font-bold text-primary mt-0.5">
                            {activeMode.power}
                          </motion.span>
                        </AnimatePresence>
                      </div>

                    </div>
                  </div>
                </div>

                {/* Mode Selector Buttons */}
                <div className="p-4 sm:p-6 bg-secondary/40 border-t border-border/40">
                  <div className="text-xs font-mono text-muted-foreground mb-3 font-semibold uppercase tracking-wider">
                    {t.selectScene}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {sceneModes.map((mode, i) => {
                    const isActive = activeMode.id === mode.id;
                    return <Button key={mode.id} data-index={i} onClick={() => setActiveModeId(mode.id)} className={`px-3 py-2.5 rounded-full text-xs font-medium transition-[color,background-color,border-color] duration-200 border text-left flex flex-col justify-between gap-1 cursor-pointer h-auto ${isActive ? "bg-primary text-primary-foreground border-primary font-semibold shadow-md" : "bg-card/60 text-card-foreground border-border/60 hover:border-primary/40 hover:bg-card"}`}>
                          <span className="truncate">{mode.label}</span>
                          <span className={`text-[10px] font-mono ${isActive ? "text-primary-foreground/80" : "text-muted-foreground"}`}>
                            {mode.lux}
                          </span>
                        </Button>;
                  })}
                  </div>

                  {/* Active Description Box */}
                  <div className="mt-4 p-3.5 rounded-lg bg-card/80 border border-border/50">
                    <AnimatePresence mode="wait">
                      <motion.p key={activeMode.id + "-desc-" + language} initial={{
                      opacity: 0
                    }} animate={{
                      opacity: 1
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="text-xs sm:text-sm text-foreground/90 font-default leading-relaxed">
                        <strong className="text-primary font-semibold mr-1.5">{activeMode.label}:</strong>
                        {activeMode.description}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Full-width Video Block - 12 Cols */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-12 space-y-3">
            <h3 className="text-xl sm:text-2xl font-sans font-bold text-foreground tracking-tight">
              {t.videoHeading}
            </h3>

            <div className="relative aspect-video w-full rounded-xl overflow-hidden border border-white/10 bg-card shadow-2xl">
              {canPlay ? <iframe src="https://www.youtube-nocookie.com/embed/JUpLmyEdA1Q?autoplay=1" title="Digital Experience Tour | Loxone Smart Home" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" /> : showConsentPrompt ? <div className="relative size-full">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/maxresdefault.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/maxresdefault.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/maxresdefault.png-wvc-sizes" alt="Digital Experience Tour | Loxone Smart Home" loading="lazy" className="size-full object-cover filter blur-[2px] brightness-50" />
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 sm:p-6">
                    <div className="max-w-lg w-full text-center space-y-4">
                      <p className="text-xs sm:text-sm text-foreground/90 font-default leading-relaxed">
                        {t.videoConsentNotice}{" "}
                        <Link to="/CookiePolicyPage" className="text-primary underline hover:text-primary/80 transition-colors inline-block">
                          {t.videoCookiePolicyLink}
                        </Link>
                        .
                      </p>
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
                        <Button type="button" onClick={handleLoadOnce} className="w-full sm:w-auto px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors shadow-md cursor-pointer">
                          {t.videoLoadOnce}
                        </Button>
                        <Button type="button" variant="outline" onClick={openPreferences} className="w-full sm:w-auto px-5 py-2.5 rounded-full text-xs sm:text-sm font-semibold border-border bg-card/80 text-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer">
                          {t.videoConfigureCookies}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div> : <div className="relative size-full group cursor-pointer" onClick={handlePlayClick}>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/maxresdefault.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/maxresdefault.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/maxresdefault.png-wvc-sizes" alt="Digital Experience Tour | Loxone Smart Home" loading="lazy" className="size-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-black/40 transition-opacity duration-300 group-hover:bg-black/30 flex items-center justify-center">
                    <div className="size-16 sm:size-20 rounded-full bg-primary/90 text-primary-foreground flex items-center justify-center shadow-lg transition-transform duration-300 group-hover:scale-110">
                      <Play className="size-8 sm:size-10 fill-current translate-x-0.5" />
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>

          {/* Features & Call to Action - 5 Cols */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="lg:col-span-5 flex flex-col justify-between h-full space-y-8">
            <div className="space-y-6">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-foreground font-default tracking-tight mb-2">
                  {t.servicesHeading}
                </h3>
                <p className="text-sm sm:text-base text-muted-foreground font-default">
                  {t.servicesDesc}
                </p>
              </div>

              <Separator className="bg-border/60" />

              {/* Feature Points List */}
              <ul className="space-y-4">
                {t.features.map((featureTitle, idx) => {
                const IconComp = FEATURE_ICONS[idx];
                return <li key={idx} data-index={idx} className="flex items-start gap-3.5">
                      <div className="mt-0.5 size-8 shrink-0 rounded-lg bg-secondary border border-white/10 flex items-center justify-center text-primary shadow-xs">
                        <IconComp className="size-4" />
                      </div>
                      <span className="text-sm sm:text-base font-default text-foreground/95 leading-snug pt-0.5">
                        {featureTitle}
                      </span>
                    </li>;
              })}
              </ul>
            </div>

            {/* Action Card Block */}
            <div className="rounded-xl border border-primary/30 bg-gradient-to-br from-primary/10 via-secondary/30 to-card p-6 shadow-xl space-y-4">
              <div className="flex items-center gap-2 text-xs font-mono font-semibold text-primary uppercase tracking-wider">
                <CheckCircle2 className="size-4 text-primary" />
                <span>{t.whatsappBadge}</span>
              </div>
              
              <p className="text-sm font-default text-muted-foreground">
                {t.whatsappParagraph}
              </p>

              <Button asChild size="lg" className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white font-bold shadow-lg shadow-primary/20 transition-[background-color,box-shadow] rounded-full">
                <Link to="https://wa.me/34662498828?text=Hola%2C%20me%20interesa%20la%20dom%C3%B3tica%20Loxone%20para%20mi%20vivienda" newTab className="flex items-center justify-center gap-2">
                  <MessageCircle className="size-5 fill-current" />
                  <span>{t.whatsappButton}</span>
                </Link>
              </Button>

              <div className="text-center">
                <span className="text-xs font-mono text-muted-foreground">
                  {t.controlFooter}
                </span>
              </div>
            </div>

          </motion.div>

        </div>

      </div>
    </section>;
}